/**
 * Aba "Contas bancárias" de /financeiro/cadastros · redesign FIN-7.
 * Cards .acct-card (fiel ao mockup); clique no card abre edição, card "+ Nova
 * conta" cria. CRUD e endpoints inalterados.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import BancoSelect from "../../../components/BancoSelect";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { obterBanco } from "../../../utils/bancos";
import {
  atualizarConta, criarConta, desativarConta, formatarReais,
  listarContas, TIPOS_CONTA,
} from "../../../services/financeiro";
import {
  Input, SecondaryButton, Select, Textarea,
} from "../../Orcamentos/styles";


function ContaForm({ inicial, onSave, onCancel, onDelete, unidadeId }) {
  const [dados, setDados] = useState({
    nome: inicial?.nome || "",
    tipo: inicial?.tipo || "CORRENTE",
    banco_nome: inicial?.banco_nome || "",
    banco_codigo: inicial?.banco_codigo || "",
    agencia: inicial?.agencia || "",
    numero_conta: inicial?.numero_conta || "",
    saldo_inicial: inicial?.saldo_inicial || "0",
    observacoes: inicial?.observacoes || "",
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Nome *</span>
        <Input value={dados.nome} onChange={(e) => setDados({ ...dados, nome: e.target.value })} maxLength={160} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Tipo *</span>
        <Select value={dados.tipo} onChange={(e) => setDados({ ...dados, tipo: e.target.value })}>
          {TIPOS_CONTA.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
        </Select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Banco</span>
        <BancoSelect value={dados.banco_codigo}
          onChange={(banco) => setDados({ ...dados, banco_codigo: banco?.codigo || "", banco_nome: banco?.nome || "" })}
          placeholder="Selecione o banco (busque por nome ou código)" />
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Agência</span>
          <Input value={dados.agencia} onChange={(e) => setDados({ ...dados, agencia: e.target.value })} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Número</span>
          <Input value={dados.numero_conta} onChange={(e) => setDados({ ...dados, numero_conta: e.target.value })} />
        </label>
      </div>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Saldo inicial</span>
        <Input type="number" step="0.01" value={dados.saldo_inicial}
               onChange={(e) => setDados({ ...dados, saldo_inicial: e.target.value })} />
        <small style={{ opacity: 0.6 }}>Saldo anterior aos lançamentos do PharuERP</small>
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Observações</span>
        <Textarea rows={2} value={dados.observacoes}
                  onChange={(e) => setDados({ ...dados, observacoes: e.target.value })} />
      </label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
        {onDelete && <AsyncButton variant="danger" silentSuccess onClick={onDelete} style={{ marginRight: "auto" }}>Desativar</AsyncButton>}
        <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton variant="primary" onClick={() => onSave({ ...dados, unidade: unidadeId })} successMessage="Conta salva">Salvar</AsyncButton>
      </div>
    </div>
  );
}


export default function ContasTab() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeManage = isMaster || hasAllPermissions(["financeiro:configurar"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarContas(request);
      const list = Array.isArray(p) ? p : p?.results || [];
      setItems(list);
      if (list.length > 0 && !unidadeId) setUnidadeId(list[0].unidade);
    } catch (err) {
      setError(err?.message || "Erro.");
    } finally { setLoading(false); }
  }, [request, unidadeId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (unidadeId) return;
    request("/api/unidades").then((d) => {
      const list = Array.isArray(d) ? d : d?.results || [];
      if (list.length > 0) setUnidadeId(list[0].id);
    }).catch(() => {});
  }, [request, unidadeId]);

  const handleSalvar = async (dados) => {
    if (editando === "novo") await criarConta(request, dados);
    else await atualizarConta(request, editando.id, dados);
    setEditando(null);
    await carregar();
  };

  const handleExcluir = async (c) => {
    if (!window.confirm(`Desativar conta "${c.nome}"?`)) return;
    await desativarConta(request, c.id);
    setEditando(null);
    await carregar();
  };

  const totalSaldo = items.reduce((s, c) => s + Number(c.saldo_atual || 0), 0);

  return (
    <>
      <div className="flex between" style={{ marginBottom: 12, alignItems: "center" }}>
        <span className="tiny mono muted">{loading ? "Carregando…" : `${items.length} conta(s)`}</span>
      </div>
      {error && <div className="empty"><div className="msg">{error}</div></div>}

      <div className="card-grid-3">
        {items.map((c) => {
          const bancoLabel = obterBanco(c.banco_codigo)?.nomeCurto || c.banco_nome || c.tipo_display;
          const meta = (c.agencia || c.numero_conta)
            ? `ag. ${c.agencia || "—"} · cc ${c.numero_conta || "—"}`
            : c.tipo_display;
          return (
            <div key={c.id} className="acct-card" role={podeManage ? "button" : undefined}
                 tabIndex={podeManage ? 0 : undefined} style={podeManage ? { cursor: "pointer" } : undefined}
                 onClick={() => podeManage && setEditando(c)}
                 onKeyDown={(e) => podeManage && e.key === "Enter" && setEditando(c)}>
              <div className="acct-top">
                <span className="acct-bank">{bancoLabel}</span>
                <span className={"badge no-dot " + (c.ativo ? "success" : "neutral")}>{c.ativo ? "Ativa" : "Inativa"}</span>
              </div>
              <div className="acct-name ell" title={c.nome}>{c.nome}</div>
              <div className="acct-meta mono ell" title={meta}>{meta}</div>
              <div className="acct-saldo"><span className="k">Saldo atual</span>{formatarReais(c.saldo_atual)}</div>
            </div>
          );
        })}
        {podeManage && (
          <div className="acct-card is-add" role="button" tabIndex={0}
               onClick={() => setEditando("novo")}
               onKeyDown={(e) => e.key === "Enter" && setEditando("novo")}>
            <FiPlus size={20} aria-hidden="true" /> <span>Nova conta</span>
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="tiny muted" style={{ marginTop: 12, fontFamily: "var(--font-mono)" }}>
          Saldo consolidado: <strong style={{ color: "var(--ink)" }}>{formatarReais(totalSaldo)}</strong> · igual ao “Saldo total” do Dashboard
        </div>
      )}

      {editando && (
        <SlideOverPanel open onClose={() => setEditando(null)}
                        title={editando === "novo" ? "Nova conta" : `Editar ${editando.nome}`}>
          <ContaForm inicial={editando === "novo" ? null : editando}
                     onSave={handleSalvar} onCancel={() => setEditando(null)}
                     onDelete={editando !== "novo" && editando.ativo ? () => handleExcluir(editando) : undefined}
                     unidadeId={unidadeId} />
        </SlideOverPanel>
      )}
    </>
  );
}
